import React, { useState } from "react";
import {
  X,
  User,
  MapPin,
  Box,
  Hash,
  Calendar,
  CreditCard,
  Receipt,
  Scale,
  Layers,
  ChevronDown,
  ChevronUp,
  Info, // <-- Added Info icon
} from "lucide-react";

const TransactionDetailsModal = ({ transaction, onClose }) => {
  const [expandedRows, setExpandedRows] = useState({});

  if (!transaction) return null;

  const { customer, purchasedProducts } = transaction;

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(Number(val || 0));

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 transition-all">
      <div className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden animate-fadeIn border border-slate-200">
        {/* Header */}
        <div className="bg-white px-6 py-5 border-b border-slate-200 flex justify-between items-start sticky top-0 z-10">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                <Receipt size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                  Transaction{" "}
                  {transaction.transactionNumber || `#${transaction.id}`}
                  {transaction.isVoid ? (
                    <span className="bg-red-100 text-red-700 text-xs px-2.5 py-0.5 rounded-full border border-red-200 font-bold uppercase tracking-wide">
                      Voided
                    </span>
                  ) : (
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-2.5 py-0.5 rounded-full border border-emerald-200 font-bold uppercase tracking-wide">
                      Completed
                    </span>
                  )}
                </h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(transaction.date).toLocaleString()}
                  </span>
                  <span className="hidden sm:inline text-slate-300">|</span>
                  <span>
                    Processed by:{" "}
                    <span className="font-semibold text-slate-700">
                      {transaction.fullName}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200 border border-transparent hover:border-red-100"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-8">
          {/* Info Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Card */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <User size={16} className="text-blue-500" /> Customer
                Information
              </h3>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-500 text-sm">Customer Name</span>
                  <span className="font-semibold text-slate-800 text-base">
                    {customer?.customerName || "Walk-In Customer"}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoItem label="Address" value={customer?.address} />
                  <InfoItem label="TIN" value={customer?.tinNumber} />
                  <InfoItem
                    label="Business Style"
                    value={customer?.businessStyle}
                  />
                  <InfoItem
                    label="Customer Type"
                    value={customer?.customerType}
                    isBadge
                    color="blue"
                  />
                </div>
              </div>
            </div>

            {/* Payment & Logistics Card */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-purple-500"></div>
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <CreditCard size={16} className="text-purple-500" /> Payment &
                Logistics
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                <InfoItem
                  label="Location"
                  value={transaction.location}
                  icon={<MapPin size={12} />}
                />
                <InfoItem
                  label="Checked By"
                  value={transaction.checkedBy || "System"}
                />
                <InfoItem
                  label="Payment Method"
                  value={transaction.paymentType}
                  isBadge
                  color="purple"
                />
                <InfoItem label="Terms" value={transaction.terms || "Cash"} />

                <div className="col-span-1 sm:col-span-2 bg-slate-50 rounded-lg p-3 border border-slate-100 flex justify-between items-center mt-1">
                  <span className="text-slate-500 text-xs font-semibold uppercase">
                    Discount Applied
                  </span>
                  <div className="text-right">
                    <span className="text-xs text-slate-500 mr-2">
                      {transaction.discountType}
                    </span>
                    <span className="font-bold text-slate-800">
                      {formatCurrency(transaction.discountAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-slate-50/80 px-5 py-3 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700 uppercase flex items-center gap-2">
                <Box size={16} className="text-slate-500" /> Purchased Items
                <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-xs ml-2">
                  {transaction.totalItems} items
                </span>
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <th className="px-5 py-3 font-semibold w-[40%]">
                      Product Description
                    </th>
                    <th className="px-5 py-3 font-semibold w-[15%]">Codes</th>
                    <th className="px-5 py-3 font-semibold text-center w-[10%]">
                      Qty / UOM
                    </th>
                    <th className="px-5 py-3 font-semibold text-right w-[15%]">
                      Price
                    </th>
                    <th className="px-5 py-3 font-semibold text-right w-[20%]">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {purchasedProducts?.map((item, idx) => {
                    const componentGroups = item.serialNumbers || [];

                    const isBom = componentGroups.some(
                      (sm) => sm.productId !== item.productId,
                    );

                    const flatNormalSerials = !isBom
                      ? componentGroups
                          .flatMap((sm) => sm.serialDetails || [])
                          .filter(
                            (sn) =>
                              sn.serialName && sn.serialName.trim() !== "",
                          )
                      : [];

                    return (
                      <React.Fragment key={item.id || idx}>
                        {/* Main Product Row */}
                        <tr
                          className={`transition-colors ${expandedRows[idx] ? "bg-indigo-50/30" : "hover:bg-slate-50/80"}`}
                        >
                          <td className="px-5 py-4 align-top">
                            <div className="font-bold text-slate-800 text-base mb-1">
                              {item.productName}
                            </div>

                            {!isBom && flatNormalSerials.length > 0 && (
                              <div className="mt-2 bg-indigo-50/50 rounded-md p-2 border border-indigo-100 inline-block max-w-full">
                                <div className="text-[10px] font-bold text-indigo-400 uppercase mb-1 flex items-center gap-1">
                                  <Hash size={10} /> Serials
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {flatNormalSerials.map((sn) => (
                                    <span
                                      key={sn.id}
                                      className="text-[10px] bg-white text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap"
                                      title={`Batch: ${sn.batchName}`}
                                    >
                                      {sn.serialName}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {isBom && componentGroups.length > 0 && (
                              <button
                                onClick={() => toggleRow(idx)}
                                className="mt-2 flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-md border border-indigo-100 transition-colors"
                              >
                                <Layers size={14} />
                                {expandedRows[idx]
                                  ? "Hide Bundle Breakdown"
                                  : "View Bundle Breakdown"}
                                {expandedRows[idx] ? (
                                  <ChevronUp size={14} className="ml-1" />
                                ) : (
                                  <ChevronDown size={14} className="ml-1" />
                                )}
                              </button>
                            )}
                          </td>
                          <td className="px-5 py-4 align-top text-xs text-slate-500 space-y-1">
                            <div className="flex items-center gap-1">
                              <span className="font-mono bg-slate-100 px-1 rounded">
                                SKU
                              </span>{" "}
                              {item.itemCode}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-mono bg-slate-100 px-1 rounded">
                                BAR
                              </span>{" "}
                              {item.barCode}
                            </div>
                          </td>
                          <td className="px-5 py-4 align-top text-center">
                            <div className="flex flex-col items-center justify-start gap-1">
                              <span className="font-bold text-slate-800 text-base">
                                {item.quantity}
                              </span>
                              {item.uom && (
                                <div
                                  className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded text-[10px] uppercase font-bold tracking-wide cursor-help"
                                  title={`Conversion Rate: ${item.conversionRate}`}
                                >
                                  <Scale size={10} />
                                  {item.uom}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-4 align-top text-right text-slate-600">
                            {formatCurrency(item.price)}
                          </td>
                          <td className="px-5 py-4 align-top text-right font-bold text-slate-800">
                            {formatCurrency(item.subtotal)}
                          </td>
                        </tr>

                        {/* Expanded BOM Details Row */}
                        {isBom && expandedRows[idx] && (
                          <tr className="bg-slate-50 border-b border-slate-200 shadow-inner">
                            <td colSpan="5" className="px-5 py-4">
                              <div className="pl-4 border-l-[3px] border-indigo-400 ml-2 space-y-3">
                                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <Box size={14} /> Components Deducted from
                                  Inventory
                                </div>
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {componentGroups.map((group, gIdx) => {
                                    const details =
                                      group.serialDetails?.filter(
                                        (sd) =>
                                          sd.serialName &&
                                          sd.serialName.trim() !== "",
                                      ) || [];

                                    // ─── MATH VARIABLES ───
                                    const bundlesPurchased = item.quantity || 0;
                                    const parentUom = item.uom || "Unit";
                                    const parentConvRate =
                                      item.conversionRate || 1;

                                    const uomCode =
                                      group.componentUomCode ||
                                      group.ComponentUomCode ||
                                      "Units";
                                    const rawQtyPerBom =
                                      group.componentQuantityPerBom ??
                                      group.ComponentQuantityPerBom ??
                                      0;

                                    // Qty required per Purchased UOM (e.g., if bought in Box, multiply base qty by box conversion rate)
                                    const qtyPerPurchasedUnit =
                                      rawQtyPerBom * parentConvRate;

                                    // If there are serials, length is the absolute truth. Otherwise, calculate expected.
                                    const totalConsumed =
                                      details.length > 0
                                        ? details.length
                                        : bundlesPurchased *
                                          qtyPerPurchasedUnit;

                                    return (
                                      <div
                                        key={group.id || gIdx}
                                        className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-indigo-200 transition-colors"
                                      >
                                        <div className="mb-3">
                                          <div className="text-sm font-bold text-slate-800 line-clamp-1 mb-2">
                                            {group.productName ||
                                              `Component ID: ${group.productId}`}
                                          </div>

                                          {/* --- EXPLANATION TEXT --- */}
                                          <div className="text-[10px] text-slate-500 mb-2 flex items-center gap-1.5 bg-slate-50 w-max px-2 py-1 rounded border border-slate-100">
                                            <Info
                                              size={12}
                                              className="text-indigo-400"
                                            />
                                            <span>
                                              1 <b>{parentUom}</b> contains{" "}
                                              <b>
                                                {qtyPerPurchasedUnit} {uomCode}
                                              </b>
                                            </span>
                                          </div>

                                          {/* --- MATH UI --- */}
                                          <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 flex items-center justify-between text-xs mb-3">
                                            <div className="flex flex-col items-center flex-1">
                                              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">
                                                Purchased
                                              </span>
                                              <span className="font-black text-slate-700">
                                                {bundlesPurchased}{" "}
                                                <span className="text-[10px] font-bold">
                                                  {parentUom}
                                                </span>
                                              </span>
                                            </div>
                                            <div className="text-slate-300 font-black px-2">
                                              ×
                                            </div>
                                            <div className="flex flex-col items-center flex-1 border-l border-r border-slate-200 px-2">
                                              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">
                                                Per {parentUom}
                                              </span>
                                              <span className="font-bold text-slate-700">
                                                {qtyPerPurchasedUnit}{" "}
                                                <span className="text-[10px] font-bold">
                                                  {uomCode}
                                                </span>
                                              </span>
                                            </div>
                                            <div className="text-slate-300 font-black px-2">
                                              =
                                            </div>
                                            <div className="flex flex-col items-center flex-1 bg-indigo-50 border border-indigo-100 px-2 py-1.5 rounded">
                                              <span className="text-[10px] text-indigo-500 uppercase font-bold tracking-wide">
                                                Total Used
                                              </span>
                                              <span className="font-black text-indigo-700">
                                                {totalConsumed}{" "}
                                                <span className="text-[10px] font-bold">
                                                  {uomCode}
                                                </span>
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Serials or Non-Serialized Badge */}
                                        {details.length > 0 ? (
                                          <div className="flex flex-wrap gap-1.5 mt-auto">
                                            {details.map((sn) => (
                                              <span
                                                key={sn.id}
                                                className="text-[10px] bg-white text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap font-mono"
                                                title={`Batch: ${sn.batchName}`}
                                              >
                                                {sn.serialName}
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="mt-auto text-[10px] font-bold tracking-wide text-slate-400 uppercase bg-slate-100 px-2 py-1 rounded inline-block self-start">
                                            Non-Serialized Item
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="flex flex-col md:flex-row justify-end items-start gap-4">
            <div className="w-full md:w-1/2"></div>
            <div className="w-full md:w-[450px] bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="p-5 space-y-3">
                <SummaryRow
                  label="Subtotal (VAT Inc.)"
                  value={formatCurrency(
                    transaction.totalAmount - transaction.vatAmount,
                  )}
                />
                <SummaryRow
                  label="VAT Amount (12%)"
                  value={formatCurrency(transaction.vatAmount)}
                />
                {transaction.ewtAmount > 0 && (
                  <SummaryRow
                    label="EWT Amount"
                    value={`- ${formatCurrency(transaction.ewtAmount)}`}
                    textClass="text-red-500"
                  />
                )}
                <div className="my-4 border-t-2 border-dashed border-slate-200"></div>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-slate-800">
                    Grand Total
                  </span>
                  <span className="text-2xl font-black text-indigo-600">
                    {formatCurrency(transaction.totalAmount)}
                  </span>
                </div>
              </div>

              <div className="bg-slate-50 px-5 py-4 border-t border-slate-200">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-slate-500">
                    Amount Paid ({transaction.paymentType})
                  </span>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(transaction.payment)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-500">
                    Change
                  </span>
                  <span className="font-bold text-emerald-600">
                    {formatCurrency(transaction.change)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-t border-slate-200 p-4 sm:px-8 flex justify-end gap-3 z-10">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-white border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm active:scale-95"
          >
            Close Window
          </button>
        </div>
      </div>
    </div>
  );
};

// Reusable components
const InfoItem = ({ label, value, isBadge, color = "blue", icon }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1">
      {icon} {label}
    </span>
    {isBadge ? (
      <span
        className={`self-start text-xs font-bold px-2.5 py-0.5 rounded border uppercase tracking-wide
         ${color === "blue" ? "bg-blue-50 text-blue-700 border-blue-200" : ""}
         ${color === "purple" ? "bg-purple-50 text-purple-700 border-purple-200" : ""}
         ${!value ? "bg-slate-100 text-slate-500 border-slate-200" : ""}
       `}
      >
        {value || "N/A"}
      </span>
    ) : (
      <span
        className="text-sm font-medium text-slate-700 truncate"
        title={value}
      >
        {value || "N/A"}
      </span>
    )}
  </div>
);

const SummaryRow = ({ label, value, textClass = "text-slate-700" }) => (
  <div className="flex justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium ${textClass}`}>{value}</span>
  </div>
);

export default TransactionDetailsModal;
